import { supabase } from "@/lib/supabaseClient";

/**
 * Cliente de la Edge Function "guia-ia": el chat de la Guía IA corre en
 * Ollama Cloud, pero la llave vive solo como secret de la función en
 * Supabase — el navegador únicamente habla con nuestra propia función
 * usando la sesión normal del usuario.
 */

export type GuiaChatMessage = { role: "user" | "assistant"; content: string };

export type GuiaReplyResult =
  | { ok: true; reply: string }
  | { ok: false; reason: "not_configured" | "auth_failed" | "session" | "error"; message: string };

const FRIENDLY_ERRORS: Record<string, { reason: "not_configured" | "auth_failed" | "session" | "error"; message: string }> = {
  ollama_not_configured: {
    reason: "not_configured",
    message: "La Guía IA todavía no está activada en este entorno. Avísale a tu administrador.",
  },
  ollama_auth_failed: {
    reason: "auth_failed",
    message: "La Guía IA no pudo autenticarse con su proveedor de IA. Avísale a tu administrador.",
  },
  invalid_session: { reason: "session", message: "Tu sesión expiró. Vuelve a iniciar sesión." },
  missing_auth: { reason: "session", message: "Tu sesión expiró. Vuelve a iniciar sesión." },
};

export async function askGuiaIa(messages: GuiaChatMessage[]): Promise<GuiaReplyResult> {
  try {
    const { data, error } = await supabase.functions.invoke("guia-ia", { body: { messages } });

    if (error) {
      // Las respuestas no-2xx llegan como error; el cuerpo trae un código legible.
      let code = "";
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === "function") {
        const body = await ctx.json().catch(() => null);
        code = body?.error ?? "";
      }
      const friendly = FRIENDLY_ERRORS[code];
      if (friendly) return { ok: false, ...friendly };
      return { ok: false, reason: "error", message: "La Guía IA no pudo responder ahora. Inténtalo de nuevo en un momento." };
    }

    const reply = typeof data?.reply === "string" ? data.reply : "";
    if (!reply) {
      return { ok: false, reason: "error", message: "La Guía IA no devolvió respuesta. Inténtalo de nuevo." };
    }
    return { ok: true, reply };
  } catch (err) {
    console.error("askGuiaIa error", err);
    return { ok: false, reason: "error", message: "No se pudo conectar con la Guía IA. Revisa tu conexión." };
  }
}
